"use client";

import { Image } from "@/helpers/Image";
import { cn } from "@/lib/utils";
import type { GROQImage } from "@/sanity/lib/definitions";
import Ticker from "./Ticker";
import { formatDelta, formatPoints, rankLabel } from "./useLiveBoard";

/** Metal of each place, as "r,g,b". */
export const METAL = { 1: "232,200,114", 2: "201,211,207", 3: "211,154,106" } as const;

/**
 * Figma geometry per place (desktop px). Every size below is an `em` of the card's font-size,
 * which is one desktop Figma px; the mobile carousel shrinks the same card (1st ×0.5499,
 * 2nd/3rd ×0.6598), exactly as the mobile frame does.
 */
const LAYOUT = {
  1: { w: 360, h: 560, imgTop: 1, imgH: 432, imgOffset: "-1.97%", name: 342, points: 377, size: 56, stats: 464, statsW: 304 },
  2: { w: 300, h: 460, imgTop: -1, imgH: 332, imgOffset: "-0.12%", name: 258, points: 293, size: 44, stats: 364, statsW: 244 },
  3: { w: 300, h: 420, imgTop: -1, imgH: 302, imgOffset: "0.08%", name: 218, points: 253, size: 44, stats: 324, statsW: 244 },
} as const;

const SCALE = { 1: "text-[0.068733rem]", 2: "text-[0.082479rem]", 3: "text-[0.082479rem]" } as const;

interface Props {
  place: 1 | 2 | 3;
  name: string;
  points: number;
  lastDay: number;
  prize?: string;
  statue?: GROQImage;
  lastDayLabel?: string;
  prizeLabel?: string;
  className?: string;
}

export default function PodiumCard({ place, name, points, lastDay, prize, statue, lastDayLabel, prizeLabel, className }: Props) {
  const l = LAYOUT[place];
  const metal = METAL[place];
  // Lengths on an element that also sets its own font-size are divided by that size (em is local).
  const em = (px: number) => `${px}em`;

  return (
    <div
      className={cn(
        "tc-anim tc-float relative shrink-0 overflow-hidden border-solid bg-[#001b14] lg:text-[0.125rem]",
        SCALE[place],
        className,
      )}
      style={{
        width: em(l.w),
        height: em(l.h),
        borderWidth: "1em",
        borderColor: `rgba(${metal},0.45)`,
        animationDelay: `${-place * 1.7}s`,
      }}>
      {statue && (
        <div className="absolute overflow-hidden" style={{ left: "-1em", top: em(l.imgTop), width: em(l.w), height: em(l.imgH) }}>
          <Image
            src={statue}
            alt=""
            sizes="(max-width: 992px) 50vw, 20vw"
            quality={90}
            className="tc-anim tc-statue absolute left-0 max-w-none origin-bottom"
            style={{ width: "100%", height: "auto", top: l.imgOffset, animationDelay: `${-place * 2.3}s` }}
          />
        </div>
      )}
      <div
        className="absolute"
        style={{
          left: "-1em",
          top: "-1em",
          width: em(l.w),
          height: em(l.h),
          backgroundImage: "linear-gradient(180deg, rgba(0,27,20,0) 38%, #001b14 66%)",
        }}
      />

      <div
        className="absolute flex border-solid bg-[rgba(0,27,20,0.7)]"
        style={{ left: "23em", top: "19em", padding: "4em 10em", borderWidth: "1em", borderColor: `rgba(${metal},0.5)` }}>
        <p
          className="mb-0 font-geist leading-[normal] font-semibold whitespace-nowrap uppercase"
          style={{ fontSize: "20em", letterSpacing: "0.06em", color: `rgb(${metal})` }}>
          {rankLabel(place)}
        </p>
      </div>

      <p
        className="absolute mb-0 font-geist leading-[normal] font-medium whitespace-nowrap text-white"
        style={{ left: em(27 / 22), top: em(l.name / 22), fontSize: "22em" }}>
        {name}
      </p>
      <p
        className="absolute mb-0 font-geist leading-[normal] font-medium whitespace-nowrap"
        style={{
          left: em(27 / l.size),
          top: em(l.points / l.size),
          fontSize: em(l.size),
          color: place === 1 ? `rgb(${metal})` : "#fff",
        }}>
        <Ticker value={formatPoints(points)} />
      </p>

      <div
        className="absolute flex items-start bg-white/10 leading-[normal] whitespace-nowrap"
        style={{ left: "27em", top: em(l.stats), width: em(l.statsW), gap: "1em" }}>
        {[
          [lastDayLabel, formatDelta(lastDay)],
          [prizeLabel, prize],
        ].map(([label, value], i) => (
          <div
            key={i}
            className="flex min-w-0 flex-1 flex-col items-start overflow-hidden bg-[#011a14]"
            style={{ padding: "10em 12em", gap: "2em" }}>
            <p className="mb-0 font-geist font-medium text-white/[0.72]" style={{ fontSize: "14em" }}>
              {label}
            </p>
            <p className="mb-0 font-geist font-semibold text-white" style={{ fontSize: "18em" }}>
              {i === 0 ? <Ticker value={value ?? ""} /> : value}
            </p>
          </div>
        ))}
      </div>

      <div
        className="tc-anim tc-bar absolute"
        style={{
          left: "-1em",
          top: em(l.h - 5),
          width: em(l.w),
          height: "4em",
          backgroundImage: `linear-gradient(90deg, rgba(${metal},0) 0%, rgb(${metal}) 50%, rgba(${metal},0) 100%)`,
          animationDelay: `${-place * 0.9}s`,
        }}
      />
    </div>
  );
}
